import { fileNameSchema } from './bulk-grant';

import {
  showAccountAcquirerSelect,
  showAccountIssuerSelect,
  showAccountWalletServiceSelect,
} from '@/components/pages/bulk-import/add/AccountBulkImport';
import {
  AccountRoleKey,
  BulkImportCategoryKey,
  BulkImportTypeKey,
} from '@/types';
import {
  InferType,
  string,
  object,
  boolean,
} from '@/validations/custom-yup';

/** カテゴリ */
export const bulkImportCategorySchema = string<BulkImportCategoryKey>()
  .label('カテゴリ')
  .required(({ label }) => `${label}を選択してください。`);

/** アクワイアラ（アカウント以外用） */
export const bulkImportAcquirerIdSchema = string()
  .label('アクワイアラ')
  .required(({ label }) => `${label}を選択してください。`);

/** アクワイアラ（アカウント用） */
export const bulkImportAcquirerIdAccountSchema = string()
  .label('アクワイアラ')
  .when(
    'role',
    ([role], schema) => (showAccountAcquirerSelect(role)
      ? schema
        .test(
          'アクワイアラをリクエストに含める権限を選択しているか',
          ({ label }) => `${label}を選択してください。`,
          (value) => (
            value != null
          ),
        )
      : schema),
  );

/** 権限（アカウントのみ） */
export const bulkImportRoleSchema = string<AccountRoleKey>()
  .label('権限')
  .required(({ label }) => `${label}を選択してください。`);

/** イシュア（アカウントのみ） */
export const bulkImportValueIssuerSchema = string()
  .label('イシュア')
  .when(
    'role',
    ([role], schema) => (showAccountIssuerSelect(role)
      ? schema
        .test(
          'イシュアをリクエストに含める権限を選択しているか',
          ({ label }) => `${label}を選択してください。`,
          (value) => (
            value != null
          ),
        )
      : schema),
  );

/** ウォレットサービス（アカウントのみ） */
export const bulkImportWalletServiceIdSchema = string()
  .label('ウォレットサービス')
  .when(
    'role',
    ([role], schema) => (showAccountWalletServiceSelect(role)
      ? schema
        .test(
          'ウォレットサービスをリクエストに含める権限を選択しているか',
          ({ label }) => `${label}を選択してください。`,
          (value) => (
            value != null
          ),
        )
      : schema),
  );

/** 登録種別 */
export const bulkImportTypeSchema = string<BulkImportTypeKey>()
  .label('登録種別')
  .required(({ label }) => `${label}を選択してください。`);

/** 登録済みの企業・店舗の更新チェック */
export const updateCheckFlagSchema = boolean()
  .label('チェックボックス')
  .required();

export const bulkImportAccountFormSchema = object({
  acquirerId: bulkImportAcquirerIdAccountSchema,
  category: bulkImportCategorySchema,
  fileName: fileNameSchema,
  role: bulkImportRoleSchema,
  tempFileName: fileNameSchema,
  type: bulkImportTypeSchema,
  valueIssuerId: bulkImportValueIssuerSchema,
  walletServiceId: bulkImportWalletServiceIdSchema,
});

export const bulkImportCompanyFormSchema = object({
  acquirerId: bulkImportAcquirerIdSchema,
  category: bulkImportCategorySchema,
  fileName: fileNameSchema,
  updateCheckFlag: updateCheckFlagSchema,
  tempFileName: fileNameSchema,
  type: bulkImportTypeSchema,
});

export const bulkImportShopFormSchema = object({
  acquirerId: bulkImportAcquirerIdSchema,
  category: bulkImportCategorySchema,
  fileName: fileNameSchema,
  updateCheckFlag: updateCheckFlagSchema,
  tempFileName: fileNameSchema,
  type: bulkImportTypeSchema,
});

export const bulkImportTerminalFormSchema = object({
  acquirerId: bulkImportAcquirerIdSchema,
  category: bulkImportCategorySchema,
  fileName: fileNameSchema,
  tempFileName: fileNameSchema,
  type: bulkImportTypeSchema,
});

export const bulkImportPartnerWalletShopFormSchema = object({
  acquirerId: bulkImportAcquirerIdSchema,
  category: bulkImportCategorySchema,
  fileName: fileNameSchema,
  tempFileName: fileNameSchema,
});

export const bulkImportShopSplitPaymentFormSchema = object({
  acquirerId: bulkImportAcquirerIdSchema,
  category: bulkImportCategorySchema,
  fileName: fileNameSchema,
  tempFileName: fileNameSchema,
});

export type BulkImportAccountForm = InferType<typeof bulkImportAccountFormSchema>;
export type BulkImportCompanyForm = InferType<typeof bulkImportCompanyFormSchema>;
export type BulkImportShopForm = InferType<typeof bulkImportShopFormSchema>;
export type BulkImportTerminalForm = InferType<typeof bulkImportTerminalFormSchema>;
export type BulkImportPartnerWalletShopForm = InferType<typeof bulkImportPartnerWalletShopFormSchema>;
export type BulkImportShopSplitPaymentForm = InferType<typeof bulkImportShopSplitPaymentFormSchema>;
